/**
 * Phase 27.37.F — Public Agent SMS Program page (A2P evidence).
 *
 * Route: /agent-sms  (public — no authentication)
 * Purpose: transparency + A2P compliance verification. Documents the
 * actual opt-in path (which is inside authenticated KR Agency Hub —
 * NOT this page). No opt-in form here.
 */
import React from 'react';
import { MessageSquare } from 'lucide-react';

export default function PublicAgentSmsPage() {
  return (
    <div className="min-h-screen bg-slate-50">
      <main className="max-w-3xl mx-auto px-6 py-12">
        <div className="mb-8 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center">
            <MessageSquare size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-heading font-semibold text-slate-900">
              KR Enterprise Group Agent SMS Program
            </h1>
            <p className="text-sm text-slate-500">A2P compliance overview — not an opt-in form.</p>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-6 text-sm text-slate-700 leading-relaxed">
          <section>
            <h2 className="text-base font-semibold text-slate-900 mb-2">About this program</h2>
            <p>
              Authorized KR Enterprise Group agents and agency users may voluntarily
              elect to receive operational SMS/MMS notifications through KR Agency Hub.
              This program is separate from our customer-facing SMS program and uses a
              dedicated Twilio Messaging Service and phone number.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-slate-900 mb-2">Program number</h2>
            <p className="font-mono text-slate-900">+1 (813) 701-2300</p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-slate-900 mb-2">How Agent SMS Opt-In Works</h2>
            <p>The opt-in path is inside the authenticated KR Agency Hub application:</p>
            <p className="mt-2 font-mono text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1.5 inline-block">
              KR Agency Hub → Administration → Notifications → Notification Preferences → Text Message (SMS/MMS)
            </p>
            <p className="mt-3">
              When the agent enables Text Message notifications, a single consent form opens
              containing <strong>both</strong> the mobile phone number field and the SMS consent
              checkbox. The agent submits both together as one atomic transaction — consent is
              tied to the specific mobile number entered on this form.
            </p>

            {/* ─── Non-functional visual preview (A2P evidence) ─── */}
            <div className="mt-6 border border-slate-300 rounded-lg overflow-hidden" data-testid="agent-sms-form-preview">
              <div className="bg-slate-100 border-b border-slate-200 px-4 py-2 text-[11px] uppercase tracking-wide text-slate-600 font-medium">
                Example of the authenticated KR Agency Hub SMS consent form
                <span className="ml-2 normal-case font-normal text-slate-500">
                  (visual only — this preview does not submit or create consent)
                </span>
              </div>
              <div className="bg-white p-5 space-y-4 opacity-95" aria-hidden="true">
                <div>
                  <div className="text-lg font-semibold text-slate-900">Enable SMS/MMS Notifications</div>
                  <div className="text-sm text-slate-500 mt-0.5">
                    Enter the mobile number that should receive SMS, then affirmatively accept the disclosure.
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-medium block">
                    Mobile Phone Number <span className="text-red-500">*</span>
                  </label>
                  <div className="w-full border border-slate-300 rounded px-3 py-2 text-sm bg-slate-50 text-slate-800 font-mono select-none">
                    (813) 555-1212
                  </div>
                  <p className="text-[11px] text-slate-500">
                    US-format numbers (10 digits) or full international format (starting with +) are
                    accepted. We normalize to E.164 (e.g. +18135551212) and store this as the
                    consent-tied number.
                  </p>
                </div>

                <div className="rounded border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700 leading-relaxed space-y-2">
                  <div className="flex items-start gap-2">
                    <div
                      className="mt-0.5 w-4 h-4 border-2 border-slate-400 rounded-sm bg-white flex-shrink-0"
                      role="presentation"
                      title="Unchecked checkbox (example)"
                    />
                    <span>
                      I agree to receive SMS/MMS messages from KR Enterprise Group at the mobile
                      number provided above.
                    </span>
                  </div>
                  <p className="pl-6">
                    Messages may include account notifications, contracting updates, follow-up
                    reminders, training/event reminders, and other operational communications.
                    Message frequency varies. Message and data rates may apply. Consent is not a
                    condition of employment, contracting, or participation. Reply STOP to opt out
                    or HELP for help.
                  </p>
                  <p className="pl-6 text-slate-500">
                    See our{' '}
                    <a href="/privacy" className="text-blue-600 underline">Privacy Policy</a>{' '}and{' '}
                    <a href="/terms" className="text-blue-600 underline">Terms &amp; Conditions</a>.
                  </p>
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <span className="inline-flex items-center px-3 py-1.5 rounded text-sm text-slate-500 border border-slate-200 bg-white select-none">
                    Cancel
                  </span>
                  <span
                    className="inline-flex items-center px-3 py-1.5 rounded text-sm text-white bg-slate-400 opacity-70 cursor-not-allowed select-none"
                    aria-disabled="true"
                    title="Disabled until phone is valid AND checkbox is checked"
                  >
                    Enable SMS Notifications
                  </span>
                </div>
              </div>
              <div className="bg-amber-50 border-t border-amber-200 px-4 py-2 text-[11px] text-amber-800">
                This is a static visual example. No data is collected, transmitted, or stored from
                this preview. The real form is only available after signing in to KR Agency Hub.
              </div>
            </div>

            <p className="mt-4">
              Authorized agents complete this consent form after signing in to KR Agency Hub under
              <span className="font-mono text-xs bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5 mx-1">
                Administration → Notifications → Notification Preferences
              </span>.
              The Mobile Phone Number field and SMS consent checkbox are presented on the same
              form and submitted together. SMS is disabled by default.
            </p>

            <ul className="list-disc pl-5 mt-3 space-y-1">
              <li>The SMS option is <strong>OFF by default</strong>. Agents are never enrolled automatically.</li>
              <li>The checkbox is never pre-selected. The agent must affirmatively check it.</li>
              <li>Consent is voluntary and is <strong>not</strong> a condition of contracting or doing business with KR Enterprise Group, or of using KR Agency Hub.</li>
              <li>Consent is tied to the exact mobile number entered on the form. Changing the phone number requires a new affirmative consent.</li>
              <li>Reply <strong>STOP</strong> to opt out (also BAJA, CANCELA, CANCELAR, ALTO, PARAR).</li>
              <li>Reply <strong>HELP</strong> for assistance (also AYUDA, INFORMACION).</li>
              <li>Initial opt-in is <strong>not</strong> performed by texting START. START/UNSTOP is honored only for re-subscription of a number that previously opted out.</li>
              <li>This public page itself does not collect SMS consent — it is documentation only.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-base font-semibold text-slate-900 mb-2">Message types</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>Contracting updates</li>
              <li>Onboarding updates</li>
              <li>Training reminders</li>
              <li>Event reminders</li>
              <li>Compliance reminders</li>
              <li>Certification reminders</li>
              <li>Follow-ups</li>
              <li>Account and operational updates</li>
              <li>Responses to agent inquiries</li>
            </ul>
          </section>

          <section>
            <h2 className="text-base font-semibold text-slate-900 mb-2">Data handling</h2>
            <p>
              Mobile phone numbers and SMS opt-in / consent data collected for this
              program are <strong>not sold, rented, shared, or transferred</strong> to
              third parties or affiliates for marketing or promotional purposes.
              Service providers such as Twilio may process messaging data on behalf of
              KR Enterprise Group solely to deliver the messages.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-slate-900 mb-2">More information</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li><a href="/privacy" className="text-blue-600 underline">Privacy Policy</a></li>
              <li><a href="/terms" className="text-blue-600 underline">Terms &amp; Conditions</a></li>
            </ul>
          </section>
        </div>

        <p className="text-xs text-slate-400 mt-6">© KR Enterprise Group</p>
      </main>
    </div>
  );
}
